import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n';

interface LanguageSwitcherProps {
  /** Compact (smaller padding/text) for dense navbars. */
  size?: 'sm' | 'md';
  /** Visual variant: light for white/gray backgrounds, dark for dark headers. */
  variant?: 'light' | 'dark';
  className?: string;
}

const isSupported = (lng: string): lng is SupportedLanguage =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(lng);

const FLAG: Record<SupportedLanguage, string> = {
  en: '🇬🇧',
  th: '🇹🇭',
};

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  size = 'md',
  variant = 'light',
  className = '',
}) => {
  const { i18n, t } = useTranslation('common');

  const currentRaw = i18n.resolvedLanguage || i18n.language || 'en';
  const current: SupportedLanguage = isSupported(currentRaw)
    ? currentRaw
    : 'en';

  const handleChange = useCallback(
    (lng: SupportedLanguage) => {
      if (lng === current) return;
      void i18n.changeLanguage(lng);
    },
    [current, i18n]
  );

  const sizeClass =
    size === 'sm' ? 'text-xs px-2 py-1' : 'text-sm px-3 py-1.5';

  const containerClass =
    variant === 'dark'
      ? 'bg-gray-800 border border-gray-700'
      : 'bg-gray-100 border border-gray-200';

  const activeClass =
    variant === 'dark'
      ? 'bg-blue-600 text-white shadow'
      : 'bg-white text-gray-900 shadow';

  const inactiveClass =
    variant === 'dark'
      ? 'text-gray-400 hover:text-white'
      : 'text-gray-500 hover:text-gray-900';

  return (
    <div
      role="group"
      aria-label={t('language.switchTo')}
      className={`inline-flex items-center rounded-lg p-0.5 ${containerClass} ${className}`}
    >
      {SUPPORTED_LANGUAGES.map((lng) => {
        const isActive = lng === current;
        return (
          <button
            key={lng}
            type="button"
            onClick={() => handleChange(lng)}
            aria-pressed={isActive}
            aria-label={
              lng === 'th'
                ? t('language.thaiName')
                : t('language.englishName')
            }
            className={`inline-flex items-center gap-1.5 rounded-md font-medium transition-colors ${sizeClass} ${
              isActive ? activeClass : inactiveClass
            }`}
          >
            <span aria-hidden="true" className="leading-none">
              {FLAG[lng]}
            </span>
            <span>{t(`language.${lng}`)}</span>
          </button>
        );
      })}
    </div>
  );
};
